import React, { useState, useRef, useMemo } from "react"
import {
	EditorState,
	RichUtils,
	getDefaultKeyBinding,
	KeyBindingUtil,
	convertToRaw,
} from "draft-js"
import Editor from "draft-js-plugins-editor"
import createMentionPlugin, {
	defaultSuggestionsFilter,
} from "draft-js-mention-plugin"
import createLinkifyPlugin from "draft-js-linkify-plugin"
import createToolbarPlugin from "draft-js-static-toolbar-plugin"
import isALoomVideo from "../util/isALoomVideo"
import type { Mention } from "../types"

let { hasCommandModifier } = KeyBindingUtil

function processKeyBinding(event: React.KeyboardEvent) {
	if (event.keyCode === 13 /* `cmd enter` key */ && hasCommandModifier(event)) {
		return "jameditor-enter"
	}
	return getDefaultKeyBinding(event)
}

let mentionsTheme = {
	mention: "mention",
	mentionSuggestions: "mentionSuggestions",
	mentionSuggestionsEntry: "mentionSuggestionsEntry",
	mentionSuggestionsEntryFocused: "mentionSuggestionsEntryFocused",
	mentionSuggestionsEntryText: "mentionSuggestionsEntryText",
	mentionSuggestionsEntryAvatar: "mentionSuggestionsEntryAvatar",
	mentionSuggestionsEntryContainer: "mentionSuggestionsEntryContainer",
	mentionSuggestionsEntryContainerRight:
		"mentionSuggestionsEntryContainerRight",
	mentionSuggestionsEntryTitle: "mentionSuggestionsEntryTitle",
}

let linkifyPlugin = createLinkifyPlugin()
let staticToolbarPlugin = createToolbarPlugin()

interface EntryProps {
	mention: Mention
	theme: {
		mentionSuggestionsEntryContainer: string
		mentionSuggestionsEntryContainerLeft: string
		mentionSuggestionsEntryAvatar: string
		mentionSuggestionsEntryContainerRight: string
		mentionSuggestionsEntryText: string
		mentionSuggestionsEntryTitle: string
	}
}

function Entry(props: EntryProps) {
	let { mention, theme, ...parentProps } = props
	return (
		<div {...parentProps}>
			<div className={theme.mentionSuggestionsEntryContainer}>
				<div className={theme.mentionSuggestionsEntryContainerLeft}>
					<img
						src={mention.avatar}
						className={theme.mentionSuggestionsEntryAvatar}
						role="presentation"
					/>
				</div>
				<div className={theme.mentionSuggestionsEntryContainerRight}>
					<div className={theme.mentionSuggestionsEntryText}>
						{mention.name}
					</div>
					<div className={theme.mentionSuggestionsEntryTitle}>
						{mention.title}
					</div>
				</div>
			</div>
		</div>
	)
}

function getEmbeddedVideoSrc(text: string) {
	if (!text.includes("loom")) return null
	if (!(text.includes("share") || text.includes("embed"))) return null
	return isALoomVideo(text)
}

export interface JamEditorProps {
	mentions: Mention[]
	editorState: EditorState
	onEditorChange: (editorState: EditorState) => void
	onMessageSend: () => void
	specificElement?: Element
}

export default function JamEditor(props: JamEditorProps) {
	let { editorState, mentions } = props
	let editorRef = useRef<Editor | null>(null)

	let mentionPlugin = useMemo(() => {
		return createMentionPlugin({
			mentions,
			entityMutability: "IMMUTABLE",
			theme: mentionsTheme,
			mentionPrefix: "@",
			supportWhitespace: true,
		})
	}, [mentions])

	let [suggestions, setSuggestions] = useState(props.mentions)
	let [readOnly, setReadOnly] = useState(false)

	let embedVideoSrc = useMemo(() => {
		let state = convertToRaw(editorState.getCurrentContent())

		for (let block of state.blocks) {
			let text = block.text
			let embedVideoSrc = getEmbeddedVideoSrc(text)
			if (embedVideoSrc !== null) return embedVideoSrc
		}

		return null
	}, [editorState])

	function handleChange(editorState: EditorState) {
		props.onEditorChange(editorState)
	}

	function handleSearchChange({ value }: any) {
		setSuggestions(defaultSuggestionsFilter(value, props.mentions))
	}

	function handleAddMention() {
		// get the mention object selected
	}

	function handleKeyCommand(command: string, editorState: EditorState) {
		if (command === "jameditor-enter") {
			setReadOnly(false)
			props.onMessageSend()
			return "handled"
		}

		let newState = RichUtils.handleKeyCommand(editorState, command)

		if (newState) {
			handleChange(newState)
			return "handled"
		}

		return "not-handled"
	}

	let MentionSuggestions = mentionPlugin.MentionSuggestions
	let plugins = [mentionPlugin, linkifyPlugin, staticToolbarPlugin]

	return (
		<div className="jam--editor-draftjs-wrapper lds-grid">
			<Editor
				editorState={props.editorState}
				handleKeyCommand={handleKeyCommand}
				onChange={handleChange}
				plugins={plugins}
				ref={(ref) => {
					editorRef.current = ref
				}}
				placeholder="Type a comment..."
				keyBindingFn={processKeyBinding}
				readOnly={readOnly}
			/>
			<MentionSuggestions
				onSearchChange={handleSearchChange}
				suggestions={suggestions}
				onAddMention={handleAddMention}
				entryComponent={Entry}
			/>
			{embedVideoSrc !== null && (
				<div className="jam--embed-video-container">
					<iframe
						width="100%"
						height="100%"
						src={embedVideoSrc}
						frameBorder="0"
						allowFullScreen
					></iframe>
					<style>{`
						.jam--embed-video-container {
							padding: 15px;
						}
					`}</style>
				</div>
			)}
			<style>
				{`
					.jam--suggest-css {
						box-sizing: border-box;
						border-radius: 5px;
						display: flex;
						align-items: center;
						justify-content: center;
						height: 30px;
						margin-left: 12px;
						font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo,
							Courier, monospace;
						font-size: 10px;
						margin-bottom: 20px;
						font-weight: 600;
						color: #47b38f;
						background: rgba(115, 229, 191, 0.15);
						border: none;
					}
					.jam--editor-draftjs-wrapper {
						width: 100%;
						padding: 0.5rem 0;
					}
					.DraftEditor-root {
						width: 100%;
						box-sizing: border-box;
						padding: 0px 1rem 1rem;
						padding-top: 0px;
						padding-right: 1rem;
						padding-bottom: 1rem;
						padding-left: 1rem;
						font-size: 14px;
						line-height: 20px;
						font-weight: 400;
						color: #2a3632;
					}

					.DraftEditor-root a {
						text-decoration: none;
						color: #3875fd;
					}

					.DraftEditor-editorContainer {
						background-color: rgba(255, 255, 255, 0);
						border-left: 0.1px solid transparent;
						position: relative;
						text-align: left;
						z-index: 1;
					}

					.draftJsToolbar__buttonWrapper__1Dmqh {
						display: inline-block;
					}

					.draftJsToolbar__button__qi1gf {
						background: #fbfbfb;
						color: #888;
						font-size: 18px;
						border: 0;
						padding-top: 5px;
						vertical-align: bottom;
						height: 34px;
						width: 36px;
					}

					.draftJsToolbar__button__qi1gf svg {
						fill: #888;
					}

					.draftJsToolbar__button__qi1gf:hover,
					.draftJsToolbar__button__qi1gf:focus {
						background: #f3f3f3;
						outline: 0; /* reset for :focus */
					}

					.draftJsToolbar__active__3qcpF {
						background: #efefef;
						color: #444;
					}

					.draftJsToolbar__active__3qcpF svg {
						fill: #444;
					}
					.draftJsToolbar__separator__3U7qt {
						display: inline-block;
						border-right: 1px solid #ddd;
						height: 24px;
						margin: 0 0.5em;
					}
					.draftJsToolbar__toolbar__dNtBH {
						border: none;
						padding: 0.25rem;
						background: #fff;
						border-radius: 2px;
						box-shadow: none;
						z-index: 2;
						box-sizing: border-box;
					}

					.draftJsToolbar__toolbar__dNtBH:after {
						border-color: rgba(255, 255, 255, 0);
						border-top-color: #fff;
						border-width: 4px;
						margin-left: -4px;
					}
					.draftJsToolbar__toolbar__dNtBH:before {
						border-color: rgba(221, 221, 221, 0);
						border-top-color: #ddd;
						border-width: 6px;
						margin-left: -6px;
					}
					.DraftEditor-alignLeft .public-DraftEditorPlaceholder-root {
						left: 0;
						text-align: left;
					}
					.DraftEditor-alignCenter .public-DraftEditorPlaceholder-root {
						margin: 0 auto;
						text-align: center;
						width: 100%;
					}
					.DraftEditor-alignRight .public-DraftEditorPlaceholder-root {
						right: 0;
						text-align: right;
					}
					.public-DraftEditorPlaceholder-root {
						color: #9197a3;
						position: absolute;
						z-index: 1;
					}

					.public-DraftEditorPlaceholder-hasFocus {
						color: #bdc1c9;
					}

					.DraftEditorPlaceholder-hidden {
						display: none;
					}

					.editor {
						box-sizing: border-box;
						border: 1px solid #ddd;
						cursor: text;
						padding: 16px;
						border-radius: 2px;
						margin-bottom: 2em;
						box-shadow: inset 0px 1px 8px -3px #ababab;
						background: #fefefe;
					}

					.editor :global(.public-DraftEditor-content) {
						min-height: 140px;
					}

					.draftJsMentionPlugin__mention__29BEd,
					.draftJsMentionPlugin__mention__29BEd:visited {
						color: #575f67;
						cursor: pointer;
						display: inline-block;
						background: #e6f3ff;
						padding-left: 2px;
						padding-right: 2px;
						border-radius: 2px;
						text-decoration: none;
					}

					.draftJsMentionPlugin__mention__29BEd:hover,
					.draftJsMentionPlugin__mention__29BEd:focus {
						color: #677584;
						background: #edf5fd;
						outline: 0;
					}

					.draftJsMentionPlugin__mention__29BEd:active {
						color: #222;
						background: #455261;
					}
					.draftJsMentionPlugin__mentionSuggestionsEntry__3mSwm {
						padding: 7px 10px 3px 10px;
					}

					.draftJsMentionPlugin__mentionSuggestionsEntry__3mSwm:active {
						background-color: #cce7ff;
					}

					.draftJsMentionPlugin__mentionSuggestionsEntryFocused__3LcTd {
						background-color: #e6f3ff;
					}

					.draftJsMentionPlugin__mentionSuggestionsEntryText__3Jobq {
						display: inline-block;
						margin-left: 8px;
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
						max-width: 368px;
						font-size: 0.9em;
						margin-bottom: 0.2em;
					}

					.draftJsMentionPlugin__mentionSuggestionsEntryAvatar__1xgA9 {
						display: inline-block;
						width: 24px;
						height: 24px;
						border-radius: 12px;
					}

					.draftJsMentionPlugin__mentionSuggestions__2DWjA {
						border: 1px solid #eee;
						margin-top: 0.4em;
						position: absolute;
						min-width: 220px;
						max-width: 440px;
						background: #fff;
						border-radius: 2px;
						box-shadow: 0px 4px 30px 0px rgba(220, 220, 220, 1);
						cursor: pointer;
						padding-top: 8px;
						padding-bottom: 8px;
						z-index: 2;
						display: -webkit-box;
						display: flex;
						-webkit-box-orient: vertical;
						-webkit-box-direction: normal;
						flex-direction: column;
						box-sizing: border-box;
						-webkit-transform: scale(0);
						transform: scale(0);
					}

					.mention {
						color: #3776fd;
						text-decoration: none;
					}

					.mentionSuggestions {
						border-top: 1px solid #eee;
						background: #fff;
						border-radius: 2px;
						cursor: pointer;
						padding-top: 8px;
						padding-bottom: 8px;
						display: flex;
						flex-direction: column;
						box-sizing: border-box;
						transform-origin: 50% 0%;
						transform: scaleY(0);
						margin: -16px;
						margin-top: 5px;
					}

					.mentionSuggestionsEntryContainer {
						display: table;
						width: 100%;
					}

					.mentionSuggestionsEntryContainerLeft,
					.mentionSuggestionsEntryContainerRight {
						display: table-cell;
						vertical-align: middle;
					}

					.mentionSuggestionsEntryContainerRight {
						width: 100%;
						padding-left: 8px;
					}

					.mentionSuggestionsEntry {
						/* padding: 7px 10px 3px 10px; */
						padding: 0;
						transition: background-color 0.4s
							cubic-bezier(0.27, 1.27, 0.48, 0.56);
						color: rgb(55, 60, 77);
					}

					.mentionSuggestionsEntry:active {
						/* background-color: #cce7ff; */
						/* font-weight: 500; */
						color: #000;
					}

					.mentionSuggestionsEntryFocused {
						composes: mentionSuggestionsEntry;
						/* background-color: #e6f3ff; */
						font-weight: 500;
					}

					.mentionSuggestionsEntryText,
					.mentionSuggestionsEntryTitle {
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
					}

					.mentionSuggestionsEntryText {
						color: rgb(55, 60, 77);
					}

					.mentionSuggestionsEntryTitle {
						font-size: 80%;
						color: #a7a7a7;
					}

					.mentionSuggestionsEntryAvatar {
						display: block;
						width: 25px;
						height: 25px;
						border-radius: 50%;
						margin-left: 25px;
						margin-top: 5px;
						margin-bottom: 5px;
					}
				`}
			</style>
		</div>
	)
}
